import React, { useEffect, useState } from "react";
import { fetchCustomerPayments } from "./CustomerPaymentAction";
import { useTranslation } from "react-i18next";

function CustomerPayment() {
  const { t, i18n } = useTranslation();
  const isRtl = ["ar", "ckb"].includes(i18n.language);

  const [customers, setCustomers] = useState({
    customer_payments: [],
    old_debt: 0,
    total_sale_invoice: 0,
    total_paid: 0,
    name: "",
    balance: 0,
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localFrom, setLocalFrom] = useState("");
  const [localTo, setLocalTo] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // Pagination State
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState({
    current_page: 1,
    next_page_url: null,
    prev_page_url: null,
  });

  // Debounce date inputs
  useEffect(() => {
    const handler = setTimeout(() => {
      setFrom(localFrom);
      setTo(localTo);
    }, 600);

    return () => {
      clearTimeout(handler);
    };
  }, [localFrom, localTo]);

  // Reset to page 1 if date filters change
  useEffect(() => {
    setPage(1);
  }, [from, to]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Pass the current page state to your API action
        const data = await fetchCustomerPayments(from, to, page);

        setCustomers({
          customer_payments: data?.customer_payments ?? [],
          old_debt: data?.customer?.old_debt ?? 0,
          code: data?.customer?.code ?? "",
          total_sale_invoice: data?.customer?.total_sale_invoice ?? 0,
          total_paid: data?.customer?.total_paid,
          name: data?.customer?.name ?? "",
          balance: data?.customer?.balance ?? 0,
        });

        // Store the pagination navigation metadata
        if (data?.paginate) {
          setPaginationData({
            current_page: data.paginate.current_page || 1,
            next_page_url: data.paginate.next_page_url,
            prev_page_url: data.paginate.prev_page_url,
          });
        }
      } catch (err) {
        console.error("Error fetching customer payments:", err);
        setError("failed_to_load");
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };
    fetchData();
  }, [from, to, page]); // Triggers fetch when page changes

  const totalAmount = (customers?.customer_payments || []).reduce(
    (sum, payment) => sum + Number(payment.amount_in_base_currency || 0),
    0
  );
  console.log(customers);
  const balanceValue = Number(customers?.balance || 0);

  /* ───── Initial Loading State ───── */
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-indigo-200 rounded-full animate-spin border-t-indigo-600" />
          </div>
          <p className="text-gray-500 font-medium animate-pulse">
            {t("loading") || "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  /* ───── Error State ───── */
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-red-700 mb-2">
            {t("error")}
          </h3>
          <p className="text-red-500">{t(error)}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 md:p-8"
    >
      <div className="max-w-5xl mx-auto">

        <div className="bg-white rounded-2xl p-5 mb-2 border border-gray-300 ">
          <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">
            {t("searchDate")}
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="flex flex-col w-full">
              <label
                htmlFor="from"
                className="text-sm font-semibold text-gray-600 uppercase tracking-wide"
              >
                {t("from")}
              </label>
              <input
                id="from"
                type="date"
                value={localFrom}
                onChange={(e) => setLocalFrom(e.target.value)}
                className="border border-gray-300 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition shadow-sm bg-white w-full"
              />
            </div>
            <div className="flex flex-col w-full">
              <label
                htmlFor="to"
                className="text-sm font-semibold text-gray-600 uppercase tracking-wide"
              >
                {t("to")}
              </label>
              <input
                id="to"
                type="date"
                value={localTo}
                onChange={(e) => setLocalTo(e.target.value)}
                className="border border-gray-300 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition shadow-sm bg-white w-full"
              />
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 flex items-center gap-4 hover:shadow-lg transition">
            <div className="flex items-center justify-center w-12 h-12 bg-indigo-100 rounded-xl shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-indigo-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <div className="flex flex-wrap items-center gap-6 min-w-0">
              {/* Name Group */}
              <div className="flex flex-col min-w-[150px] flex-1">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                  {t("name")}
                </span>
                <span className="text-lg font-bold text-gray-900 truncate" title={customers?.name}>
                  {customers?.name || "—"}
                </span>
              </div>

              {/* Divider Line */}
              <div className="hidden sm:block h-8 w-px bg-gray-200 self-end mb-1" />

              {/* Customer Code Group */}
              <div className="flex flex-col min-w-[120px] flex-1">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                  {t("customer_code")}
                </span>
                <span className="text-lg font-bold text-gray-900 truncate" title={customers?.code}>
                  {customers?.code || "—"}
                </span>
              </div>
            </div>
          </div>

          {/* <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 flex items-center gap-4 hover:shadow-lg transition">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-xl shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t("mdin")}
              </p>
              <p className="text-lg font-bold text-red-600">
                {combined} <span className="text-sm font-medium">$</span>
              </p>
            </div>
          </div> */}

          {/* <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 flex items-center gap-4 hover:shadow-lg transition">
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-xl shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t("dain")}
              </p>
              <p className="text-lg font-bold text-green-600">
                {Number(customers?.total_paid || 0).toFixed(2)}{" "}
                <span className="text-sm font-medium">$</span>
              </p>
            </div>
          </div> */}

          <div
            className={`rounded-2xl shadow-md border p-5 flex items-center gap-4 hover:shadow-lg transition ${balanceValue > 0
                ? "bg-gradient-to-br from-red-50 to-red-100 border-red-200"
                : balanceValue < 0
                  ? "bg-gradient-to-br from-green-50 to-green-100 border-green-200"
                  : "bg-white border-gray-100"
              }`}
          >
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl shrink-0 ${balanceValue > 0
                  ? "bg-red-200"
                  : balanceValue < 0
                    ? "bg-green-200"
                    : "bg-gray-100"
                }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-6 w-6 ${balanceValue > 0
                    ? "text-red-700"
                    : balanceValue < 0
                      ? "text-green-700"
                      : "text-gray-600"
                  }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t("balance")}
              </p>
              <p
                className={`text-xl font-extrabold ${balanceValue > 0
                    ? "text-red-700"
                    : balanceValue < 0
                      ? "text-green-700"
                      : "text-gray-700"
                  }`}
              >
                {balanceValue.toFixed(2)}{" "}
                <span className="text-sm font-medium">$</span>
              </p>
            </div>
          </div>
        </div>

        {/* PAYMENTS TABLE */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-indigo-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
              />
            </svg>
            <h3 className="text-base font-bold text-gray-700">
              {t("payment_history") || "Payment History"}
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[450px] sm:min-w-[600px] border-collapse text-xs sm:text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                  <th className="py-2.5 px-2 sm:py-3 sm:px-5 text-center font-semibold text-[10px] sm:text-xs uppercase tracking-wider">#</th>
                  <th className="py-2.5 px-2 sm:py-3 sm:px-5 text-center font-semibold text-[10px] sm:text-xs uppercase tracking-wider">{t("invoice")}</th>
                  <th className="py-2.5 px-2 sm:py-3 sm:px-5 text-center font-semibold text-[10px] sm:text-xs uppercase tracking-wider">{t("date")}</th>
                  <th className="py-2.5 px-2 sm:py-3 sm:px-5 text-center font-semibold text-[10px] sm:text-xs uppercase tracking-wider">{t("note")}</th>
                  <th className="py-2.5 px-2 sm:py-3 sm:px-5 text-center font-semibold text-[10px] sm:text-xs uppercase tracking-wider">{t("amount")}</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-indigo-200 rounded-full animate-spin border-t-indigo-600" />
                        <p className="text-gray-400 font-medium">{t("loading") || "Loading..."}</p>
                      </div>
                    </td>
                  </tr>
                ) : customers?.customer_payments?.length > 0 ? (
                  customers.customer_payments.map((payment, index) => (
                    <tr
                      key={payment.id || index}
                      className={`transition hover:bg-indigo-50/60 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/70"
                        }`}
                    >
                      <td className="py-2 px-1.5 sm:py-3 sm:px-5 text-center border-b border-gray-100 text-[11px] sm:text-sm">
                        <span className="inline-flex items-center justify-center bg-gray-100 text-gray-600 font-bold rounded-full w-6 h-6 sm:w-7 sm:h-7 text-[10px] sm:text-xs">
                          {index + 1}
                        </span>
                      </td>
                      <td className="py-2 px-1.5 sm:py-3 sm:px-5 text-center border-b border-gray-100 text-[11px] sm:text-sm">
                        <span className="inline-block bg-indigo-50 text-indigo-700 text-[10px] sm:text-xs font-semibold px-2 py-0.5 sm:px-3 sm:py-1 rounded-lg">
                          {payment?.invoice_number || "—"}
                        </span>
                      </td>
                      <td className="py-2 px-1.5 sm:py-3 sm:px-5 text-center border-b border-gray-100 text-gray-600 text-[11px] sm:text-sm">
                        <div className="flex items-center justify-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {payment?.date ? new Date(payment.date).toLocaleDateString(i18n.language === "ckb" ? "ku-IQ" : i18n.language) : "—"}
                        </div>
                      </td>
                      <td className="py-2 px-1.5 sm:py-3 sm:px-5 text-center border-b border-gray-100 text-gray-500 max-w-[150px] sm:max-w-[200px] text-[11px] sm:text-sm">
                        {payment?.note ? (
                          <span className="inline-block bg-amber-50 text-amber-700 text-[10px] sm:text-xs px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded-lg border border-amber-200 truncate max-w-full">
                            {payment.note}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="py-2 px-1.5 sm:py-3 sm:px-5 text-center border-b border-gray-100 text-[11px] sm:text-sm">
                        <span className="font-bold text-green-700 text-sm sm:text-base">
                          {payment?.amount_in_base_currency ? Number(payment.amount_in_base_currency).toLocaleString() : "0"}
                        </span>
                        <span className="text-[10px] sm:text-xs text-gray-400 ml-1">$</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-16 w-16 text-gray-200"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="text-gray-400 text-lg font-medium">{t("no_data")}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>

              {/* {customers?.customer_payments?.length > 0 && (
                <tfoot>
                  <tr className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                    <td colSpan={4} className="py-2.5 px-2 sm:py-3 sm:px-5 text-center font-bold text-xs sm:text-sm uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/xl"
                          className="h-3.5 w-3.5 sm:h-4 sm:w-4 opacity-70"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        {t("total")}
                      </div>
                    </td>
                    <td className="py-2.5 px-2 sm:py-3 sm:px-5 text-center font-extrabold text-sm sm:text-lg">
                      {totalAmount.toLocaleString()} <span className="text-xs sm:text-sm font-medium opacity-80">$</span>
                    </td>
                  </tr>
                </tfoot>
              )} */}
            </table>
          </div>

          {/* ───── Simple Pagination Navigation Bar ───── */}
          {customers?.customer_payments?.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between flex-row-reverse">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={!paginationData.prev_page_url}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:hover:bg-white transition"
                >
                  {t("previous")}
                </button>
                <button
                  type="button"
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={!paginationData.next_page_url}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:hover:bg-white transition"
                >
                  {t("next")}
                </button>
              </div>
              <div className="text-sm text-gray-500 font-medium">
                {t("page")} <span className="font-bold text-gray-800">{paginationData.current_page}</span>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default CustomerPayment;